import { useState, useEffect, useCallback } from 'react';
import { fetchClientsByIds } from '../lib/airtable.js';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for managing conditional client selection based on program type
 * @param {Object} soRecord - Scheduled Offering record from Airtable
 * @returns {Object} Hook state and actions for conditional client management
 */
export function useConditionalClient(soRecord) {
  const [state, setState] = useState({
    programType: 'loading',
    participatingClients: [],
    selectedClient: null,
    isLoading: false,
    error: null,
    showDropdown: false
  });

  /**
   * Detect program type based on SO Client Name field
   * Client Name is a formula/lookup field that contains the actual client name string
   * (e.g., "OPEN-PROGRAM" or "Acme Corp"), not the record ID
   */
  const detectProgramType = useCallback((soRecord) => {
    // Check Client Name field (formula/lookup) for "OPEN-PROGRAM" string
    const clientName = soRecord?.fields?.['Client Name'];

    if (!clientName) {
      return { type: 'regular', showDropdown: false };
    }

    // Normalize to string (Client Name can be array from lookup or string from formula)
    const clientNameValue = Array.isArray(clientName) ? clientName[0] : clientName;

    // Check if Client Name contains "OPEN-PROGRAM" (case-insensitive)
    const isOpenProgram = typeof clientNameValue === 'string' &&
                          clientNameValue.toUpperCase().includes('OPEN-PROGRAM');

    return {
      type: isOpenProgram ? 'open' : 'regular',
      showDropdown: isOpenProgram
    };
  }, []);

  /**
   * Fetch participating clients from Airtable for open programs
   */
  const fetchParticipatingClients = useCallback(async (participatingClientIds) => {
    if (!Array.isArray(participatingClientIds) || participatingClientIds.length === 0) {
      throw new Error('Ask Ops to add the "Participating Clients"');
    }

    try {
      // ✅ CORRECT: Fetch ONLY the specific clients by their IDs
      // Uses fetchClientsByIds which filters server-side with Airtable's filterByFormula
      const participatingClients = await fetchClientsByIds(participatingClientIds);

      if (participatingClients.length === 0) {
        throw new Error('Ask Ops to add the "Participating Clients"');
      }

      // Validate client data structure
      const validClients = participatingClients.filter(client => {
        return client && client.id && client.name;
      });

      if (validClients.length === 0) {
        throw new Error('Invalid client data received');
      }

      return validClients;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'useConditionalClient', operation: 'fetchParticipatingClients' },
        extra: { participatingClientIds }
      });
      throw error;
    }
  }, []);

  /**
   * Get client from sessionStorage cache
   */
  const getCachedData = useCallback((cacheKey) => {
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;

      const data = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired (5 minutes TTL)
      if (now - data.timestamp > 300000) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }
      
      return data.value;
    } catch (error) {
      // Ignore cache errors and continue without cache
      return null;
    }
  }, []);

  /**
   * Set data in sessionStorage cache
   */
  const setCachedData = useCallback((cacheKey, value) => {
    try {
      const cacheData = {
        value,
        timestamp: Date.now()
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      // Ignore cache errors and continue without cache
    }
  }, []);

  /**
   * Load participating clients with caching
   */
  const loadParticipatingClients = useCallback(async (soRecord) => {
    const soNumber = soRecord?.fields?.SO || soRecord?.fields?.SOID;
    if (!soNumber) {
      throw new Error('Invalid SO record');
    }

    const participatingClientIds = soRecord.fields['Participating Clients'];
    if (!participatingClientIds) {
      throw new Error('Ask Ops to add the "Participating Clients"');
    }

    const cacheKey = `participating-clients-${soNumber}`;
    
    // Try cache first
    const cachedClients = getCachedData(cacheKey);
    if (cachedClients) {
      return cachedClients;
    }

    // Fetch from API if not cached
    const clients = await fetchParticipatingClients(participatingClientIds);
    setCachedData(cacheKey, clients);
    
    return clients;
  }, [getCachedData, setCachedData, fetchParticipatingClients]);

  /**
   * Get auto-assigned client for regular programs
   */
  const getAutoAssignedClient = useCallback(async (soRecord) => {
    if (!soRecord?.fields?.Client) {
      return null; // Allow null client for regular programs with missing data
    }

    const clientField = soRecord.fields.Client;
    const clientIds = Array.isArray(clientField) ? clientField : [clientField];
    
    // Filter out "OPEN-PROGRAM" entries and get actual client IDs
    const actualClientIds = clientIds.filter(id => 
      typeof id === 'string' && !id.includes('OPEN-PROGRAM')
    );

    if (actualClientIds.length === 0) {
      return null;
    }

    try {
      // Get the client name from the SO record's Client Name field
      const clientName = soRecord.fields['Client Name'];
      if (clientName) {
        return {
          id: actualClientIds[0],
          name: clientName
        };
      }

      // Fallback: Fetch specific client by ID if Client Name is missing
      const clients = await fetchClientsByIds([actualClientIds[0]]);
      const matchedClient = clients.length > 0 ? clients[0] : null;

      return matchedClient || null;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'useConditionalClient', operation: 'getAutoAssignedClient' },
        extra: { soRecord: soRecord?.id, clientIds: actualClientIds }
      });
      // Return null to allow graceful degradation
      return null;
    }
  }, []);

  // Actions
  const selectClient = useCallback((client) => {
    setState(prev => ({
      ...prev,
      selectedClient: client,
      error: null
    }));
  }, []);

  const clearSelection = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedClient: null,
      error: null
    }));
  }, []);

  const retry = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
      isLoading: true
    }));
  }, []);

  // Main effect to handle SO record changes
  useEffect(() => {
    let isMounted = true;

    const initializeClientState = async () => {
      if (!soRecord) {
        setState({
          programType: 'regular',
          participatingClients: [],
          selectedClient: null,
          isLoading: false,
          error: null,
          showDropdown: false
        });
        return;
      }

      try {
        // Detect program type
        const { type } = detectProgramType(soRecord);
        
        if (!isMounted) return;

        if (type === 'open') {
          // Open program: Load participating clients
          setState(prev => ({
            ...prev,
            programType: 'open',
            showDropdown: true,
            isLoading: true,
            error: null
          }));

          try {
            const clients = await loadParticipatingClients(soRecord);
            
            if (!isMounted) return;

            setState(prev => ({
              ...prev,
              participatingClients: clients,
              isLoading: false,
              selectedClient: null // Reset selection when clients change
            }));
          } catch (error) {
            if (!isMounted) return;

            setState(prev => ({
              ...prev,
              participatingClients: [],
              isLoading: false,
              error: error.message || 'Failed to load participating clients',
              selectedClient: null,
              showDropdown: error.message === 'Ask Ops to add the "Participating Clients"' ? false : true
            }));
          }
        } else {
          // Regular program: Auto-assign client
          setState(prev => ({
            ...prev,
            programType: 'regular',
            showDropdown: false,
            isLoading: true,
            error: null
          }));

          try {
            const autoAssignedClient = await getAutoAssignedClient(soRecord);
            
            if (!isMounted) return;

            setState(prev => ({
              ...prev,
              selectedClient: autoAssignedClient,
              isLoading: false,
              participatingClients: [] // Clear any previous clients
            }));
          } catch (error) {
            if (!isMounted) return;

            setState(prev => ({
              ...prev,
              selectedClient: null,
              isLoading: false,
              error: null // Don't show error for missing regular client data
            }));
          }
        }
      } catch (error) {
        if (!isMounted) return;

        Sentry.captureException(error, {
          tags: { component: 'useConditionalClient', operation: 'initializeClientState' },
          extra: { soRecord: soRecord?.id }
        });

        setState(prev => ({
          ...prev,
          programType: 'regular',
          showDropdown: false,
          isLoading: false,
          error: 'Failed to initialize client state',
          selectedClient: null,
          participatingClients: []
        }));
      }
    };

    initializeClientState();

    return () => {
      isMounted = false;
    };
  }, [soRecord, detectProgramType, loadParticipatingClients, getAutoAssignedClient]);

  // Effect to handle retry
  useEffect(() => {
    if (state.error && state.isLoading) {
      // Re-trigger initialization when retry is called
      const timer = setTimeout(() => {
        setState(prev => ({
          ...prev,
          isLoading: false
        }));
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [state.error, state.isLoading]);

  return {
    // State
    programType: state.programType,
    participatingClients: state.participatingClients,
    selectedClient: state.selectedClient,
    isLoading: state.isLoading,
    error: state.error,
    showDropdown: state.showDropdown,
    
    // Actions
    selectClient,
    clearSelection,
    retry
  };
}